import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTable } from 'spacetimedb/react';
import { tables } from '@vidmakr/bindings';
import {
  AtSign,
  Bell,
  BellOff,
  CheckCheck,
  CheckCircle2,
  Download,
  History,
  MessageSquare,
  MessageSquareReply,
  PencilLine,
  Send,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';
import {
  CATEGORY_LABELS,
  EMAIL_FREQUENCIES,
  EMAIL_FREQUENCY_LABELS,
  NOTIFICATION_CATEGORIES,
  notificationPath,
  serializeNotificationPrefs,
  type EmailFrequency,
  type NotificationCategory,
  type NotificationPrefs,
} from '@vidmakr/shared';
import { useNotificationPrefs, useNotifications, markAllRead, markRead, removeNotifications, desktopEnabled, desktopSupported, setDesktopEnabled, type AppNotification } from '@/lib/notifications';
import { useSession } from '@/lib/session';
import { reducers } from '@/lib/stdb';
import { attempt, toast } from '@/lib/toast';
import { timeAgo, tsDate } from '@/lib/format';
import { Avatar, Button, Card, Select, Toggle, cx, stopPortalContextMenu } from './ui';

function KindIcon({ kind }: { kind: string }) {
  const cls = 'shrink-0';
  switch (kind) {
    case 'mention':
      return <AtSign size={12} className={cls} />;
    case 'reply':
      return <MessageSquareReply size={12} className={cls} />;
    case 'review_requested':
      return <Send size={12} className={cls} />;
    case 'review_approved':
      return <CheckCircle2 size={12} className={cx(cls, 'text-ok')} />;
    case 'changes_requested':
      return <TriangleAlert size={12} className={cx(cls, 'text-warn')} />;
    case 'export_done':
      return <Download size={12} className={cx(cls, 'text-ok')} />;
    case 'export_failed':
      return <TriangleAlert size={12} className={cx(cls, 'text-danger')} />;
    case 'edited':
      return <PencilLine size={12} className={cls} />;
    case 'version_saved':
    case 'version_restored':
      return <History size={12} className={cls} />;
    default:
      return <MessageSquare size={12} className={cls} />;
  }
}

function dayLabel(n: AppNotification): string {
  const d = tsDate(n.createdAt)!;
  const today = new Date();
  const yesterday = new Date(Date.now() - 86400_000);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
}

/** Opens what a notification is about (and marks it read). */
export function useOpenNotification() {
  const navigate = useNavigate();
  return (n: AppNotification) => {
    if (!n.readAt) void markRead([n.id]);
    navigate(notificationPath(n));
  };
}

function NotificationRow({ n, onOpen }: { n: AppNotification; onOpen: (n: AppNotification) => void }) {
  const unread = !n.readAt;
  return (
    <div
      className={cx('group relative flex cursor-pointer gap-2.5 px-3 py-2.5 hover:bg-panel-3', unread && 'bg-accent/[0.06]')}
      onClick={() => onOpen(n)}
      data-notification={n.id}
      data-unread={unread || undefined}
    >
      <div className="relative">
        <Avatar name={n.actorName || 'vidmakr'} url={n.actorAvatar} size={28} />
        <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full border border-line bg-panel-2 text-muted">
          <KindIcon kind={n.kind} />
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div className={cx('text-[13px] leading-snug', unread ? 'font-semibold text-fg' : 'text-muted')}>{n.title}</div>
        {n.body ? <div className="mt-0.5 line-clamp-2 break-words text-xs text-muted">{n.body}</div> : null}
        <div className="mt-0.5 text-[11px] text-faint">{timeAgo(n.createdAt)}</div>
      </div>
      <div className="flex shrink-0 flex-col items-center gap-1">
        {unread ? <span className="mt-1.5 h-2 w-2 rounded-full bg-accent" aria-label="Unread" /> : null}
        <div className="hidden flex-col gap-0.5 group-hover:flex [@media(pointer:coarse)]:flex">
          <button
            className="rounded p-0.5 text-faint hover:bg-panel-2 hover:text-fg"
            title={unread ? 'Mark as read' : 'Mark as unread'}
            onClick={(e) => {
              e.stopPropagation();
              void markRead([n.id], unread);
            }}
          >
            <CheckCheck size={13} />
          </button>
          <button
            className="rounded p-0.5 text-faint hover:bg-panel-2 hover:text-danger"
            title="Remove"
            onClick={(e) => {
              e.stopPropagation();
              void removeNotifications([n.id]);
            }}
          >
            <X size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

function NotificationPanel({ onClose }: { onClose: () => void }) {
  const { list, unread } = useNotifications();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const open = useOpenNotification();
  const navigate = useNavigate();
  const shown = filter === 'unread' ? list.filter((n) => !n.readAt) : list;
  const groups = useMemo(() => {
    const out: { label: string; items: AppNotification[] }[] = [];
    for (const n of shown.slice(0, 150)) {
      const label = dayLabel(n);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(n);
      else out.push({ label, items: [n] });
    }
    return out;
  }, [shown]);
  const read = list.filter((n) => n.readAt);
  return (
    <div className="flex max-h-[min(560px,calc(100vh-80px))] w-[min(380px,calc(100vw-16px))] flex-col">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
        <span className="font-semibold">Notifications</span>
        <div className="ml-1 flex rounded-lg bg-panel-3 p-0.5 text-xs">
          {(['all', 'unread'] as const).map((f) => (
            <button key={f} className={cx('rounded-md px-2 py-0.5', filter === f ? 'bg-panel text-fg' : 'text-muted hover:text-fg')} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : `Unread${unread ? ` (${unread})` : ''}`}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <Button variant="ghost" size="icon-sm" title="Mark all as read" disabled={!unread} onClick={() => void markAllRead()} data-mark-all-read>
          <CheckCheck size={15} />
        </Button>
        <Button variant="ghost" size="icon-sm" title="Clear read notifications" disabled={!read.length} onClick={() => void removeNotifications(read.slice(0, 500).map((n) => n.id))}>
          <Trash2 size={14} />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto">
        {groups.length ? (
          groups.map((g) => (
            <div key={g.label}>
              <div className="sticky top-0 z-10 bg-panel-2 px-3 pb-1 pt-2 text-[11px] font-medium uppercase tracking-wide text-faint">{g.label}</div>
              {g.items.map((n) => (
                <NotificationRow
                  key={n.id}
                  n={n}
                  onOpen={(x) => {
                    onClose();
                    open(x);
                  }}
                />
              ))}
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center gap-2 px-6 py-12 text-center text-sm text-muted">
            <Bell size={22} className="text-faint" />
            {filter === 'unread' ? 'You are all caught up.' : 'Nothing yet. Comments, mentions, reviews and exports on projects you follow and media you share show up here.'}
          </div>
        )}
      </div>
      <div className="border-t border-line px-3 py-2 text-right">
        <button
          className="text-xs text-muted hover:text-fg"
          onClick={() => {
            onClose();
            navigate('/account#notifications');
          }}
        >
          Notification settings
        </button>
      </div>
    </div>
  );
}

/** The bell with the unread count; opens the notification list. */
export function NotificationBell({ align = 'right', className }: { align?: 'left' | 'right'; className?: string }) {
  const { unread } = useNotifications();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<CSSProperties | null>(null);
  const ref = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open) return setPos(null);
    const t = ref.current?.getBoundingClientRect();
    const p = panel.current;
    if (!t || !p) return;
    const w = p.offsetWidth;
    const left = align === 'right' ? t.right - w : t.left;
    setPos({ top: t.bottom + 6, left: Math.max(8, Math.min(left, window.innerWidth - w - 8)) });
  }, [open, align]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!ref.current?.contains(target) && !panel.current?.contains(target)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const close = () => setOpen(false);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', close);
    };
  }, [open]);
  return (
    <>
      <button
        ref={ref}
        className={cx('relative inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted hover:bg-panel-3 hover:text-fg', open && 'bg-panel-3 text-fg', className)}
        aria-label={unread ? `Notifications (${unread} unread)` : 'Notifications'}
        title="Notifications"
        onClick={() => setOpen((o) => !o)}
        data-notification-bell
      >
        <Bell size={16} />
        {unread ? (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold leading-none text-white" data-unread-count>
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>
      {open
        ? createPortal(
            <div ref={panel} onContextMenu={stopPortalContextMenu} className="fixed z-[70] overflow-hidden rounded-xl border border-line bg-panel-2 shadow-2xl" style={pos ?? { top: 0, left: 0, visibility: 'hidden' }}>
              <NotificationPanel onClose={() => setOpen(false)} />
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/**
 * Tells about notifications arriving while the app is open: a toast, and a
 * desktop notification when the tab is in the background (if turned on).
 * Also keeps the unread count in the tab title.
 */
export function NotificationAlerts() {
  const mountedAt = useRef(Date.now());
  const open = useOpenNotification();
  const openRef = useRef(open);
  openRef.current = open;
  const { unread } = useNotifications();
  useTable(tables.notification, {
    onInsert: (n) => {
      // Rows from the initial subscription are not news.
      const at = tsDate(n.createdAt)!.getTime();
      if (!n.inApp || n.readAt || at < mountedAt.current - 5000 || Date.now() - at > 60_000) return;
      if (document.visibilityState === 'visible') {
        toast.info(n.title);
      } else if (desktopEnabled()) {
        try {
          const d = new Notification(n.title, { body: n.body, tag: n.id, icon: n.actorAvatar || '/favicon.svg' });
          d.onclick = () => {
            window.focus();
            openRef.current(n);
            d.close();
          };
        } catch {
          /* some browsers only allow notifications from a service worker */
        }
      }
    },
  });
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\+?\)\s*/, '');
    document.title = unread ? `(${unread > 99 ? '99+' : unread}) ${base}` : base;
  }, [unread]);
  return null;
}

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export function NotificationSettingsCard() {
  const prefs = useNotificationPrefs();
  const email = useSession((s) => s.user?.email);
  const [desktop, setDesktop] = useState(desktopEnabled());
  const save = (next: NotificationPrefs) => attempt(() => reducers().setNotificationPrefs({ prefs: serializeNotificationPrefs(next) }));
  const setFlag = (channel: 'inApp' | 'email', c: NotificationCategory, v: boolean) => save({ ...prefs, [channel]: { ...prefs[channel], [c]: v } });
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (window.location.hash === '#notifications') ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const emailOff = prefs.emailFrequency === 'off';
  return (
    <div ref={ref} id="notifications" className="mt-6 max-w-xl scroll-mt-6">
      <Card className="p-5">
        <div className="mb-1 flex items-center gap-2">
          <Bell size={16} className="text-muted" />
          <h2 className="font-semibold">Notifications</h2>
        </div>
        <p className="mb-4 text-xs text-faint">
          You follow projects you create, comment on or send for review. Follow or mute any project from its Comments panel.
        </p>
        <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-5 gap-y-3 text-sm">
          <span className="text-xs font-medium text-faint">Tell me about</span>
          <span className="text-xs font-medium text-faint">In the app</span>
          <span className="text-xs font-medium text-faint">By email</span>
          {NOTIFICATION_CATEGORIES.map((c) => (
            <CategoryRow key={c} c={c} prefs={prefs} emailOff={emailOff} onChange={setFlag} />
          ))}
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-line pt-4">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium">Email</div>
            <div className="text-xs text-faint">
              {emailOff ? 'No notification emails.' : `To ${email ?? 'your address'}. Anything you read in the app first is not emailed.`}
            </div>
          </div>
          <Select
            value={prefs.emailFrequency}
            onChange={(e) => void save({ ...prefs, emailFrequency: e.target.value as EmailFrequency })}
            className="w-auto"
            aria-label="Email frequency"
            data-email-frequency
          >
            {EMAIL_FREQUENCIES.map((f) => (
              <option key={f} value={f}>
                {EMAIL_FREQUENCY_LABELS[f]}
              </option>
            ))}
          </Select>
        </div>
        {desktopSupported() ? (
          <div className="mt-4 flex items-center gap-3 border-t border-line pt-4">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">Desktop notifications</div>
              <div className="text-xs text-faint">On this browser, while vidmakr is open in a background tab.</div>
            </div>
            <Toggle
              checked={desktop}
              onChange={async (v) => {
                const on = await setDesktopEnabled(v);
                setDesktop(on);
                if (v && !on) toast.error('The browser blocked notifications. Allow them in the site settings first.');
              }}
            />
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function CategoryRow({
  c,
  prefs,
  emailOff,
  onChange,
}: {
  c: NotificationCategory;
  prefs: NotificationPrefs;
  emailOff: boolean;
  onChange: (channel: 'inApp' | 'email', c: NotificationCategory, v: boolean) => void;
}) {
  return (
    <>
      <div className="min-w-0">
        <div className="font-medium">{CATEGORY_LABELS[c].label}</div>
        <div className="text-xs text-faint">{CATEGORY_LABELS[c].hint}</div>
      </div>
      <div className="justify-self-center" data-pref={`inApp:${c}`}>
        <Toggle checked={prefs.inApp[c]} onChange={(v) => onChange('inApp', c, v)} />
      </div>
      <div className="justify-self-center" data-pref={`email:${c}`}>
        <Toggle checked={prefs.email[c] && !emailOff} disabled={emailOff} onChange={(v) => onChange('email', c, v)} />
      </div>
    </>
  );
}

/** Follow / mute a project (Comments panel). */
export function WatchButton({ project, following, level }: { project: { id: string }; following: boolean; level: string | null }) {
  return (
    <Button
      size="sm"
      variant="ghost"
      title={following ? 'You get notified about all comments, reviews and changes. Click to only hear about mentions and replies.' : 'Get notified about all comments, reviews and changes'}
      onClick={() =>
        void attempt(
          () => reducers().setProjectWatch({ projectId: project.id, level: following ? 'mute' : 'all' }),
          following ? 'Unfollowed: only mentions and replies now' : 'Following this project',
        )
      }
      data-watch={level ?? (following ? 'creator' : 'none')}
    >
      {following ? <Bell size={13} /> : <BellOff size={13} />}
      {following ? 'Following' : 'Follow'}
    </Button>
  );
}
